// 8K キャンバスの基準寸法（仕様で固定）
export const CANVAS_WIDTH_PX = 7680;
export const CANVAS_HEIGHT_PX = 4320;

// グリッドの間隔（薄い点線）
export const CANVAS_GRID_PX = 50;

// ズームの上下限
// 最大ズームインで 640x480 の領域が画面いっぱいに表示される（8K の 1/12）
// 最大ズームアウトで 7680x4320 全体が画面に収まる（実際の倍率はビューポートサイズで決まる）
export const ZOOM_MIN = 0.05; // 1/20。最小ズームの目安
export const ZOOM_MAX = 12; // 8K / 640 の比率上限

// 段階ズーム（Ctrl + ホイール / Ctrl + プラスマイナス / +- ボタン）
export const ZOOM_STEPS: readonly number[] = [
  0.0625, 0.083, 0.125, 0.167, 0.25, 0.333, 0.5, 0.667, 1, 1.5, 2, 3, 4, 6, 8, 12,
];

export interface Point2D {
  readonly x: number;
  readonly y: number;
}

export interface ViewState {
  readonly panX: number; // ステージ左上の screen 座標 (px)
  readonly panY: number;
  readonly zoom: number; // 0..ZOOM_MAX
}

/**
 * screen 座標 (clientX, clientY) を stage 座標へ変換する。
 */
export function screenToStage(point: Point2D, view: ViewState): Point2D {
  return {
    x: (point.x - view.panX) / view.zoom,
    y: (point.y - view.panY) / view.zoom,
  };
}

/**
 * stage 座標を screen 座標へ変換する。
 */
export function stageToScreen(point: Point2D, view: ViewState): Point2D {
  return {
    x: point.x * view.zoom + view.panX,
    y: point.y * view.zoom + view.panY,
  };
}

/**
 * 指定 stage 点を中心に倍率を変更する。マウス位置を中心とするズームに使用。
 */
export function zoomAt(view: ViewState, screenPoint: Point2D, nextZoom: number): ViewState {
  const stagePoint = screenToStage(screenPoint, view);
  const clampedZoom = Math.min(Math.max(nextZoom, ZOOM_MIN), ZOOM_MAX);
  return {
    zoom: clampedZoom,
    panX: screenPoint.x - stagePoint.x * clampedZoom,
    panY: screenPoint.y - stagePoint.y * clampedZoom,
  };
}

/**
 * 段階ズーム（一段拡大）を返す。
 */
export function stepZoomIn(currentZoom: number): number {
  const next = ZOOM_STEPS.find((z) => z > currentZoom + 1e-6);
  return next ?? ZOOM_MAX;
}

/**
 * 段階ズーム（一段縮小）を返す。
 */
export function stepZoomOut(currentZoom: number): number {
  const reversed = [...ZOOM_STEPS].reverse();
  const prev = reversed.find((z) => z < currentZoom - 1e-6);
  return prev ?? ZOOM_MIN;
}
